import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalRecuperarPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Por favor, introduza um email válido.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/recuperar-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSent(true);
        toast.success('Email enviado com sucesso!');
      } else {
        setError(data.detail || 'Erro ao enviar email. Tente novamente.');
      }
    } catch (err) {
      setError('Erro de ligação. Verifique a sua internet e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-[#0066CC]" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Recuperar Password
          </CardTitle>
          <CardDescription>
            Introduza o seu email para receber instruções de recuperação
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {sent ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-800">Email Enviado!</h3>
                <p className="text-slate-600 mt-2">
                  Se o email <strong>{email}</strong> estiver registado no sistema, 
                  receberá instruções para redefinir a sua password.
                </p>
              </div>
              <p className="text-sm text-slate-500">
                Verifique também a pasta de spam/lixo.
              </p>
              <Link to="/portal/login">
                <Button variant="outline" className="mt-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar ao Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email da Instituição</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@instituicao.pt"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  data-testid="input-recuperar-email"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#0066CC] hover:bg-[#0055AA]"
                disabled={loading}
                data-testid="btn-recuperar-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A enviar...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Instruções
                  </>
                )}
              </Button>

              <div className="text-center">
                <Link 
                  to="/portal/login" 
                  className="text-sm text-[#0066CC] hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Voltar ao Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <footer className="fixed bottom-4 text-center text-slate-500 text-sm w-full">
        © {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.
      </footer>
    </div>
  );
}
